import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { searchSpotifyArtists } from "../api/spotifyArtistApi";
import "../styles/lineup.css";

const DEBOUNCE_MS = 300;
const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n);

const recognizeLink = (url) => {
  if (!url) return null;
  if (url.includes("spotify.com")) return "Spotify";
  if (url.includes("bandcamp.com")) return "Bandcamp";
  if (url.includes("soundcloud.com")) return "SoundCloud";
  if (url.includes("music.apple.com")) return "Apple Music";
  return "website";
};

const spotifyIdFromUrl = (url) => (url.match(/open\.spotify\.com\/artist\/([A-Za-z0-9]+)/) || [])[1] || null;

const ArtistProfileSummary = ({ profile }) => (
  <>
    {profile.image
      ? <img className="add-artist-match__avatar" src={profile.image} alt="" />
      : <span className="add-artist-match__avatar add-artist-match__avatar--placeholder" aria-hidden="true" />}
    <span className="add-artist-match__text">
      <span className="add-artist-match__name">{profile.name}</span>
      <span className="add-artist-match__meta">
        {fmt(profile.followers)} followers · {profile.popularity} popularity{profile.genres && profile.genres.length ? ` · ${profile.genres.join(", ")}` : ""}
      </span>
    </span>
  </>
);

ArtistProfileSummary.propTypes = { profile: PropTypes.object.isRequired };

// In-place "New artist" create panel: auto Spotify lookup from the name, a
// match picker (followers + popularity + genres), a paste-a-link fallback, and
// the duplicate guards + "Add without Spotify?" nudge. Mounted by
// V3PerformerPicker when the user chooses to create rather than link.
const V3NewArtistPanel = ({ teamId, initialName, actType, submitting, onBack, onSubmit }) => {
  const [name, setName] = useState(initialName);
  const [status, setStatus] = useState("idle"); // idle | loading | matches | empty
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteUrl, setPasteUrl] = useState("");
  const [nudge, setNudge] = useState(false);
  const [takeover, setTakeover] = useState(false);
  const debounceRef = useRef(null);
  const reqRef = useRef(0);

  const runSearch = (q) => {
    const trimmed = q.trim();
    if (!trimmed) { setStatus("idle"); setMatches([]); return; }
    setStatus("loading");
    const myReq = ++reqRef.current;
    searchSpotifyArtists(teamId, trimmed)
      .then((rows) => {
        if (myReq !== reqRef.current) return;
        const list = Array.isArray(rows) ? rows : [];
        setMatches(list);
        setStatus(list.length ? "matches" : "empty");
      })
      .catch(() => { if (myReq === reqRef.current) { setMatches([]); setStatus("empty"); } });
  };

  useEffect(() => {
    if (selected) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(name), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [name, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickMatch = (m) => { setSelected(m); setTakeover(false); setNudge(false); };
  const clearSelection = () => { setSelected(null); setTakeover(false); setMatches([]); setStatus("loading"); };

  const linkedDuplicate = selected && selected.linked_artist ? selected.linked_artist : null;
  const nameMismatch = selected && (selected.name || "").trim().toLowerCase() !== name.trim().toLowerCase();
  const canAdd = !submitting && name.trim().length > 0 && (!linkedDuplicate || takeover);

  const doSubmit = () => {
    const pasteVisible = pasteMode || status === "empty";
    const pastedIsSpotify = pasteVisible && recognizeLink(pasteUrl) === "Spotify";
    const effectiveSpotifyId = selected && (!linkedDuplicate || takeover)
      ? selected.spotify_id
      : (pastedIsSpotify ? spotifyIdFromUrl(pasteUrl) : null);
    onSubmit({
      newArtistName: name.trim(),
      actType,
      spotifyId: effectiveSpotifyId,
      spotifyTakeover: !!(linkedDuplicate && takeover),
      externalUrl: pasteVisible && recognizeLink(pasteUrl) !== "Spotify" ? pasteUrl.trim() || null : null,
    });
  };

  const handleSkipSpotify = () => {
    if (submitting || name.trim().length === 0) return;
    onSubmit({ newArtistName: name.trim(), actType, spotifyId: null, spotifyTakeover: false, externalUrl: null });
  };

  const handleAdd = () => {
    if (!canAdd) return;
    // Nudge once: Spotify left empty but the lookup DID return matches.
    if (!selected && !pasteUrl && status === "matches" && !nudge) { setNudge(true); return; }
    doSubmit();
  };

  return (
    <div className="add-artist-new-panel" data-testid="new-artist-panel">
      <button type="button" className="add-artist-back" onClick={onBack}>← Back to search</button>
      <h3 className="add-artist-new-panel__title">New artist</h3>
      <p className="add-artist-new-panel__sub">
        Adding to your CRM as {actType === "headline" ? "headliner" : "support"} on this offer.
      </p>

      <label className="add-artist-field">
        <span>Artist name</span>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-artist-name" />
      </label>

      <div className="add-artist-spotify">
        <div className="add-artist-spotify__head">
          <span>Spotify</span>
          <span className="add-artist-spotify__hint">{selected ? "Selected profile" : "Optional"}</span>
        </div>

        {selected && (
          <div className="add-artist-match add-artist-match--selected" data-testid="selected-profile">
            <ArtistProfileSummary profile={selected} />
            <button type="button" className="add-artist-match__change" data-testid="change-profile" onClick={clearSelection}>Change</button>
          </div>
        )}

        {selected && nameMismatch && !linkedDuplicate && (
          <div className="add-artist-info" data-testid="name-mismatch-note">
            Adding as “{name.trim()}” with the Spotify profile “{selected.name}”.
          </div>
        )}

        {!selected && status === "loading" && <div className="add-artist-spotify__loading">Searching Spotify for “{name}”…</div>}

        {!selected && status === "matches" && !pasteMode && matches.map((m) => (
          <button
            key={m.spotify_id}
            type="button"
            data-testid="spotify-match"
            className="add-artist-match"
            onClick={() => pickMatch(m)}
          >
            <ArtistProfileSummary profile={m} />
          </button>
        ))}

        {linkedDuplicate && !takeover && (
          <div className="add-artist-error" data-testid="hard-spotify-dup">
            This Spotify profile is already linked to {linkedDuplicate.name}. Two artists can’t share a profile.
            <div className="add-artist-error__actions">
              <button
                type="button"
                className="add-artist-btn add-artist-btn--primary"
                data-testid="spotify-takeover-confirm"
                onClick={() => setTakeover(true)}
              >
                Move it here
              </button>
            </div>
          </div>
        )}

        {linkedDuplicate && takeover && (
          <div className="add-artist-warn" data-testid="spotify-takeover-chosen">
            Will move this Spotify profile from {linkedDuplicate.name} to {name.trim()} when you add.{" "}
            <button type="button" onClick={() => setTakeover(false)}>Undo</button>
          </div>
        )}

        {!selected && status === "empty" && !pasteMode && (
          <div className="add-artist-spotify__empty">No Spotify profile found for “{name}”. Paste a link if you have one — any artist page works.</div>
        )}

        {!selected && (pasteMode || status === "empty") && (
          <div className="add-artist-paste">
            <input
              type="url"
              data-testid="paste-link-input"
              placeholder="https://…"
              value={pasteUrl}
              onChange={(e) => setPasteUrl(e.target.value)}
            />
            {pasteUrl && <small>Recognized as {recognizeLink(pasteUrl)}.</small>}
            <small>Spotify, Bandcamp, SoundCloud, Apple Music or a website.</small>
          </div>
        )}

        {!selected && status === "matches" && !pasteMode && (
          <div className="add-artist-spotify__actions">
            <button type="button" onClick={() => setPasteMode(true)}>Paste a link instead</button>
          </div>
        )}

        <div className="add-artist-spotify__skip">
          <button type="button" className="add-artist-skip-btn" data-testid="skip-spotify" onClick={handleSkipSpotify}>
            Skip Spotify — just add the name
          </button>
        </div>
      </div>

      <div className="add-artist-new-panel__footer">
        <button type="button" className="add-artist-btn add-artist-btn--secondary" onClick={onBack}>Cancel</button>
        <button type="button" className="add-artist-btn add-artist-btn--primary" data-testid="new-artist-submit" disabled={!canAdd} onClick={handleAdd}>Add artist</button>
      </div>

      {nudge && (
        <div className="add-artist-nudge" data-testid="add-without-spotify">
          <h4>Add without Spotify?</h4>
          <p>Without a linked profile, {name.trim()} won’t pull artist images, listener data, or fan-targeting audiences for marketing.</p>
          <div className="add-artist-nudge__actions">
            <button type="button" className="add-artist-btn add-artist-btn--primary" onClick={() => { setNudge(false); doSubmit(); }}>Add without it</button>
            <button type="button" className="add-artist-btn add-artist-btn--secondary" onClick={() => setNudge(false)}>Search Spotify again</button>
          </div>
        </div>
      )}
    </div>
  );
};

V3NewArtistPanel.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  initialName: PropTypes.string.isRequired,
  actType: PropTypes.string,
  submitting: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

V3NewArtistPanel.defaultProps = { actType: "support", submitting: false };

export default V3NewArtistPanel;
